import { z } from "zod";
import { TimeRangeSchema } from "./query";
import type { VizType } from "./viz";

export const LayoutItemSchema = z.object({
  i: z.string(),
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().min(1),
  h: z.number().int().min(1),
});
export type LayoutItem = z.infer<typeof LayoutItemSchema>;

export const BREAKPOINTS = ["lg", "md", "sm"] as const;

export const DashboardLayoutSchema = z.object({
  lg: z.array(LayoutItemSchema).default([]),
  md: z.array(LayoutItemSchema).default([]),
  sm: z.array(LayoutItemSchema).default([]),
});

export const DashboardItemSchema = z.object({
  /** Layout key (instance of a widget on this dashboard). */
  i: z.string(),
  widgetId: z.string(),
});

/**
 * A dashboard's saved date range. Locations are never saved: which ones to show is each viewer's
 * pick, from their own access, at view time. Bodies saved before that still carry `locationIds`,
 * which parsing strips.
 */
export const GlobalFiltersSchema = z.object({
  timePreset: z.string().optional(),
  timeRange: TimeRangeSchema.optional(),
});
export type GlobalFilters = z.infer<typeof GlobalFiltersSchema>;

export const DashboardBodySchema = z.object({
  title: z.string().min(1).max(120),
  items: z.array(DashboardItemSchema).max(60).default([]),
  layout: DashboardLayoutSchema.default({ lg: [], md: [], sm: [] }),
  globalFilters: GlobalFiltersSchema.default({}),
});
export type DashboardBody = z.infer<typeof DashboardBodySchema>;

/**
 * Who a dashboard is shared with. Sharing grants view access to the dashboard, never to data:
 * every viewer still sees only their own locations.
 */
export const SHARE_TARGET_TYPES = ["org", "location", "user"] as const;
export const ShareTargetSchema = z.object({
  type: z.enum(SHARE_TARGET_TYPES),
  id: z.string().min(1).max(200),
  /** Display name, filled in by the server. */
  label: z.string().optional(),
});
export type ShareTarget = z.infer<typeof ShareTargetSchema>;
export const MAX_SHARE_TARGETS = 100;

export interface Dashboard extends DashboardBody {
  id: string;
  ownerId: string;
  orgId: string;
  createdAt: string;
  updatedAt: string;
  /** Bumped by every save; a save must send the revision it was based on. */
  revision: number;
  /** Present when the viewer is not the owner. */
  sharedBy?: string;
  /**
   * The viewer's answer to the share, present with `sharedBy`. Only accepted shares go in the
   * sidebar; pending and dismissed ones wait on the shared-with-me page.
   */
  shareStatus?: ShareStatus;
  /**
   * Where the viewer dragged it within its sidebar section or folder, top first. Absent until they
   * place it; unplaced ones sit above the placed ones, newest first.
   */
  sidebarPosition?: number;
  /** The viewer's sidebar folder it's in. Absent: under Mine, or Shared with me. */
  sidebarFolderId?: string;
  /** Present when it was installed from the marketplace. */
  installedFrom?: InstalledFrom;
}

export type ShareStatus = "pending" | "accepted" | "dismissed";

/** One of a person's own sidebar folders. Holds their dashboards and accepted shares, one level deep. */
export interface SidebarFolder {
  id: string;
  name: string;
  position: number;
}
export const SidebarFolderNameSchema = z.string().trim().min(1).max(60);
export const MAX_SIDEBAR_FOLDERS = 50;

/** The marketplace listing an installed copy came from. Installed copies never change on their own. */
export interface InstalledFrom {
  listingId: string;
  version: number;
  /** The listing's current version, when it's still listed. Newer than `version` means an update is available. */
  latestVersion?: number;
}

/** Grid columns per breakpoint (react-grid-layout in the web app). */
export const GRID_COLS = { lg: 12, md: 8, sm: 4 } as const;

/** Size a new widget starts at, by visualization. */
export const DEFAULT_WIDGET_SIZE: Record<VizType, { w: number; h: number }> = {
  kpi: { w: 3, h: 4 }, line: { w: 6, h: 8 }, area: { w: 6, h: 8 }, bar: { w: 6, h: 8 },
  combo: { w: 6, h: 8 }, pie: { w: 4, h: 8 }, heatmap: { w: 6, h: 9 }, scatter: { w: 6, h: 8 }, treemap: { w: 6, h: 8 }, funnel: { w: 6, h: 8 },
  table: { w: 6, h: 9 }, pivot: { w: 12, h: 9 },
};

/** Append a widget to the bottom of a dashboard. The grid compacts `md`/`sm` upward on render. */
export function appendWidget(body: DashboardBody, widgetId: string, viz: VizType, i = crypto.randomUUID().slice(0, 8)): DashboardBody {
  const size = DEFAULT_WIDGET_SIZE[viz];
  const bottom = Math.max(0, ...body.layout.lg.map((l) => l.y + l.h));
  return {
    ...body,
    items: [...body.items, { i, widgetId }],
    layout: {
      lg: [...body.layout.lg, { i, x: 0, y: bottom, ...size }],
      md: [...body.layout.md, { i, x: 0, y: 1e6, w: Math.min(size.w, GRID_COLS.md), h: size.h }],
      sm: [...body.layout.sm, { i, x: 0, y: 1e6, w: GRID_COLS.sm, h: size.h }],
    },
  };
}

// ---- tiling ----------------------------------------------------------------------------------------

/** One widget in a tiled row. Omitted widths share the row's free columns; omitted heights use the default. */
export interface TileCell {
  i: string;
  viz: VizType;
  w?: number;
  h?: number;
}

/** Split `total` columns into `n` near-equal integer widths, the extra columns going to the first. */
function share(total: number, n: number): number[] {
  return Array.from({ length: n }, (_, k) => Math.floor(total / n) + (k < total % n ? 1 : 0));
}

function chunk<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let k = 0; k < xs.length; k += size) out.push(xs.slice(k, k + size));
  return out;
}

/**
 * Lay rows of widgets out on every breakpoint. On `lg` each row fills the 12 columns (explicit
 * widths are kept, the rest share what's left) and every widget in a row gets the row's height,
 * so rows line up without ragged gaps. `md` and `sm` re-flow the same rows for narrower screens:
 * KPIs four (`md`) or two (`sm`) across, other widgets two across or full width.
 * Throws if a row's explicit widths don't fit.
 */
export function tileLayout(rows: TileCell[][]): DashboardBody["layout"] {
  const layout: DashboardBody["layout"] = { lg: [], md: [], sm: [] };
  const y = { lg: 0, md: 0, sm: 0 };
  /** Place `cells` left to right at `widths` on one breakpoint's next row. */
  const place = (bp: keyof typeof y, cells: TileCell[], widths: number[], h: number) => {
    let x = 0;
    cells.forEach((c, k) => {
      layout[bp].push({ i: c.i, x, y: y[bp], w: widths[k]!, h });
      x += widths[k]!;
    });
    y[bp] += h;
  };

  for (const row of rows.filter((r) => r.length)) {
    const h = Math.max(...row.map((c) => c.h ?? DEFAULT_WIDGET_SIZE[c.viz].h));
    const fixed = row.reduce((sum, c) => sum + (c.w ?? 0), 0);
    const free = row.filter((c) => c.w === undefined).length;
    if (fixed + free > GRID_COLS.lg) throw new Error(`A row's widths add up to more than ${GRID_COLS.lg} columns.`);
    const shares = share(GRID_COLS.lg - fixed, free);
    place("lg", row, row.map((c) => c.w ?? shares.shift()!), h);

    const allKpi = row.every((c) => c.viz === "kpi");
    for (const part of chunk(row, allKpi ? 4 : 2)) place("md", part, share(GRID_COLS.md, part.length), h);
    for (const part of chunk(row, allKpi ? 2 : 1)) place("sm", part, share(GRID_COLS.sm, part.length), h);
  }
  return layout;
}

/**
 * A sensible arrangement when nobody says otherwise: KPIs first in balanced rows of up to four,
 * then the other widgets in order, packed side by side at their default widths.
 */
export function autoTileRows(cells: { i: string; viz: VizType }[]): TileCell[][] {
  const kpis = cells.filter((c) => c.viz === "kpi");
  const rest = cells.filter((c) => c.viz !== "kpi");
  const perKpiRow = Math.ceil(kpis.length / Math.ceil(kpis.length / 4));
  const rows: TileCell[][] = kpis.length ? chunk(kpis, perKpiRow) : [];
  let row: TileCell[] = [];
  let used = 0;
  for (const c of rest) {
    const w = DEFAULT_WIDGET_SIZE[c.viz].w;
    if (row.length && used + w > GRID_COLS.lg) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(c);
    used += w;
  }
  if (row.length) rows.push(row);
  return rows;
}
